import { useState, useEffect, useCallback, useRef } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
    ComputerIcon as Monitor, Mic01Icon as Mic, MicOff01Icon as MicOff, ArrowDown01Icon as ChevronDown, SquareIcon as Square, Loading01Icon as Loader2,
    Layout01Icon as AppWindow, RefreshIcon as RefreshCw, Cancel01Icon as X, Tick01Icon as Check, Clock01Icon as Timer,
    Folder01Icon as FolderOpen, Film01Icon as Film, GridIcon as LayoutGrid, Delete02Icon as Trash2, PlayIcon as Play, PauseIcon as Pause, Camera01Icon as Camera, VolumeHighIcon as Volume2, VolumeMute01Icon as VolumeX, CropIcon as Crop
} from 'hugeicons-react'
import { useNavigate } from 'react-router-dom'
import { useDesktopRecorder, type DesktopSource } from '@/hooks/useDesktopRecorder'
import { useProjectStore } from '@/store/project-store'

const el = () => (window as any).electron

const BAR_H = 56
const BAR_W = 900
const MINI_W = 158    // width of the mini recording pill
const MINI_H = 48     // height of the mini recording pill
const PANEL_H = 464   // total window height when source/projects panel is open
const DEVICES_H = 440 // total window height when devices panel is open

// ─── Divider ─────────────────────────────────────────────────────────────────
function Div() {
    return <div className="w-px h-5 bg-white/[0.07] shrink-0 mx-0.5" />
}

// ─── Bar icon button ──────────────────────────────────────────────────────────
function BarBtn({ onClick, active = false, danger = false, title, children }: {
    onClick: () => void; active?: boolean; danger?: boolean; title?: string; children: React.ReactNode
}) {
    return (
        <button onClick={onClick} title={title}
            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all shrink-0 ${
                danger ? 'text-white/30 hover:text-red-400 hover:bg-red-500/10'
                    : active ? 'text-white bg-white/10'
                    : 'text-white/30 hover:text-white hover:bg-white/8'
            }`}>
            {children}
        </button>
    )
}


// ─── Source Tile ──────────────────────────────────────────────────────────────
function SourceTile({ source, selected, onSelect }: {
    source: DesktopSource; selected: boolean; onSelect: () => void
}) {
    const isScreen = source.id.startsWith('screen:')
    return (
        <button onClick={onSelect}
            className={`group flex flex-col gap-1.5 p-1.5 rounded-xl border-2 transition-all text-left ${
                selected ? 'border-brand-500 bg-brand-500/10' : 'border-white/[0.07] bg-white/[0.03] hover:border-white/20'
            }`}>
            <div className="w-full aspect-video rounded-lg overflow-hidden bg-black/60 relative">
                {source.thumbnailDataUrl
                    ? <img src={source.thumbnailDataUrl} alt={source.name} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-white/20">
                        {isScreen ? <Monitor size={18} /> : <AppWindow size={18} />}
                      </div>}
                {selected && (
                    <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-brand-500 flex items-center justify-center">
                        <Check size={9} className="text-white" />
                    </div>
                )}
            </div>
            <span className={`text-[10px] font-medium truncate px-0.5 ${selected ? 'text-white' : 'text-white/50'}`}>
                {source.name}
            </span>
        </button>
    )
}

// ─── Source Picker Panel ───────────────────────────────────────────────────────
function SourcePickerPanel({ sources, selected, isLoading, onSelect, onRefresh, cropRect, onSelectArea }: {
    sources: DesktopSource[]; selected: string | null; isLoading: boolean
    onSelect: (id: string) => void; onRefresh: () => void
    cropRect: { x: number; y: number; w: number; h: number } | null
    onSelectArea: () => void
}) {
    const screens = sources.filter(s => s.id.startsWith('screen:'))
    const windows = sources.filter(s => !s.id.startsWith('screen:'))
    const [tab, setTab] = useState<'display' | 'windows' | 'area'>(cropRect ? 'area' : 'display')
    const activeTab = tab === 'display' ? screens : windows

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-4 pt-3 pb-2 shrink-0">
                <div className="flex items-center gap-1 bg-white/[0.05] rounded-xl p-1">
                    {(['display', 'windows', 'area'] as const).map(t => (
                        <button key={t} onClick={() => setTab(t)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                tab === t ? 'bg-white/10 text-white' : 'text-white/30 hover:text-white'
                            }`}>
                            {t === 'display' ? <Monitor size={11} /> : t === 'windows' ? <AppWindow size={11} /> : <Crop size={11} />}
                            {t === 'display' ? 'Display' : t === 'windows' ? 'Windows' : 'Area'}
                            {t !== 'area' && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                    tab === t ? 'bg-brand-500/30 text-brand-300' : 'bg-white/10 text-white/30'
                                }`}>
                                    {t === 'display' ? screens.length : windows.length}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
                {tab !== 'area' && (
                    <button onClick={onRefresh} disabled={isLoading}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] text-white/30 hover:text-white hover:bg-white/5 transition-all disabled:opacity-40">
                        <RefreshCw size={11} className={isLoading ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                )}
            </div>

            {tab === 'area' ? (
                <div className="flex-1 flex flex-col items-center justify-center gap-4 px-4 pb-4">
                    {cropRect ? (
                        <>
                            <div className="flex flex-col items-center gap-1.5">
                                <div className="w-10 h-10 rounded-full bg-brand-500/15 border border-brand-500/30 flex items-center justify-center">
                                    <Check size={18} className="text-brand-400" />
                                </div>
                                <span className="text-sm font-semibold text-white">{cropRect.w} × {cropRect.h}</span>
                                <span className="text-[11px] text-white/30">Area selected</span>
                            </div>
                            <button onClick={onSelectArea}
                                className="flex items-center gap-2 h-8 px-4 rounded-xl bg-white/[0.06] border border-white/[0.10] text-xs font-semibold text-white/60 hover:text-white hover:bg-white/10 transition-all">
                                <Crop size={11} />
                                Redraw Selection
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="flex flex-col items-center gap-1.5 text-center">
                                <Crop size={22} className="text-white/20" />
                                <span className="text-sm font-semibold text-white/60">Select a region</span>
                                <span className="text-[11px] text-white/30 max-w-[200px] leading-relaxed">
                                    Draw a rectangle on your screen to record only that area
                                </span>
                            </div>
                            <button onClick={onSelectArea}
                                className="flex items-center gap-2 h-9 px-5 rounded-full bg-brand-500 hover:bg-brand-400 text-white text-sm font-semibold transition-all shadow-lg shadow-brand-500/20">
                                <Crop size={13} />
                                Draw Selection
                            </button>
                        </>
                    )}
                </div>
            ) : (
                <div className="flex-1 overflow-y-auto px-4 pb-4">
                    {isLoading && (
                        <div className="flex items-center justify-center h-28 gap-2 text-white/30">
                            <Loader2 size={16} className="animate-spin" /><span className="text-sm">Loading…</span>
                        </div>
                    )}
                    {!isLoading && activeTab.length === 0 && (
                        <div className="flex flex-col items-center justify-center h-28 gap-2 text-white/20">
                            {tab === 'display' ? <Monitor size={22} /> : <AppWindow size={22} />}
                            <span className="text-xs">No {tab === 'display' ? 'displays' : 'windows'} found</span>
                        </div>
                    )}
                    {!isLoading && activeTab.length > 0 && (
                        <div className={`grid gap-2 ${tab === 'display' ? 'grid-cols-3' : 'grid-cols-4'}`}>
                            {activeTab.map(s => (
                                <SourceTile key={s.id} source={s} selected={selected === s.id}
                                    onSelect={() => onSelect(s.id)} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

// ─── Projects Panel ─────────────────────────────────────────────────────────
function ProjectsPanel({ onClose }: { onClose: () => void }) {
    const { projects, loadProjects, loadProject, deleteProject } = useProjectStore()
    const navigate = useNavigate()

    useEffect(() => { loadProjects() }, [loadProjects])

    const handleOpen = async (id: string) => {
        await loadProject(id)
        onClose()
        navigate('/editor')
    }

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-4 pt-3 pb-2 shrink-0">
                <span className="text-xs font-semibold text-white">Projects</span>
                <span className="text-[10px] text-white/30">{projects.length} recording{projects.length !== 1 ? 's' : ''}</span>
            </div>

            {projects.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center gap-2 text-white/20">
                    <LayoutGrid size={24} />
                    <span className="text-xs">No recordings yet</span>
                </div>
            ) : (
                <div className="flex-1 overflow-y-auto px-4 pb-4">
                    <div className="grid grid-cols-4 gap-2">
                        {projects.map(p => (
                            <div key={p.id} className="group relative flex flex-col gap-1.5 p-1.5 rounded-xl border border-white/[0.07] bg-white/[0.03] hover:border-white/20 transition-all">
                                <button onClick={() => handleOpen(p.id)} className="w-full">
                                    <div className="w-full aspect-video rounded-lg overflow-hidden bg-black/60 flex items-center justify-center">
                                        <Film size={16} className="text-white/20" />
                                    </div>
                                </button>
                                <div className="flex items-center justify-between px-0.5">
                                    <span className="text-[10px] font-medium text-white/60 truncate flex-1">{p.name}</span>
                                    <button onClick={() => deleteProject(p.id)} title="Delete"
                                        className="opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded text-white/30 hover:text-red-400 transition-all shrink-0">
                                        <Trash2 size={9} />
                                    </button>
                                </div>
                                <button onClick={() => handleOpen(p.id)}
                                    className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 flex items-center justify-center bg-black/40 transition-all">
                                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur rounded-full text-[11px] font-semibold text-white">
                                        <Play size={9} fill="currentColor" />Open
                                    </div>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

// ─── Shared device row ───────────────────────────────────────────────────────
function DeviceRow({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
    return (
        <button onClick={onClick}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs text-left transition-all border ${
                selected
                    ? 'bg-brand-500/15 text-white border-brand-500/30'
                    : 'bg-white/[0.03] text-white/50 border-white/[0.06] hover:bg-white/[0.06] hover:text-white/80'
            }`}>
            <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? 'border-brand-400' : 'border-white/20'}`}>
                {selected && <div className="w-1.5 h-1.5 rounded-full bg-brand-400" />}
            </div>
            <span className="truncate">{label}</span>
        </button>
    )
}

// ─── Mic Panel ───────────────────────────────────────────────────────────────
function MicPanel({ audioDevices, selectedMicId, onSelectMic }: {
    audioDevices: MediaDeviceInfo[]; selectedMicId: string | null
    onSelectMic: (id: string) => void
}) {
    return (
        <div className="flex flex-col h-full overflow-y-auto px-4 py-4 gap-3">
            <div className="flex items-center gap-2 shrink-0">
                <Mic size={11} className="text-white/40" />
                <span className="text-[10px] font-bold text-white/40 uppercase tracking-widest">Microphone</span>
            </div>
            <div className="space-y-1">
                {audioDevices.length === 0
                    ? <p className="text-xs text-white/20 px-1">No microphones found</p>
                    : audioDevices.map((d, i) => (
                        <DeviceRow key={d.deviceId}
                            label={d.label || `Microphone ${i + 1}`}
                            selected={selectedMicId ? selectedMicId === d.deviceId : i === 0}
                            onClick={() => onSelectMic(d.deviceId)} />
                    ))
                }
            </div>
        </div>
    )
}

// ─── Camera Panel ────────────────────────────────────────────────────────────
function CameraPanel({ videoDevices, selectedCameraId, onSelectCamera }: {
    videoDevices: MediaDeviceInfo[]; selectedCameraId: string | null
    onSelectCamera: (id: string) => void
}) {
    return (
        <div className="flex flex-col h-full overflow-y-auto px-4 py-4 gap-3">
            <div className="flex items-center gap-2 shrink-0">
                <Camera size={11} className="text-white/40" />
                <span className="text-[10px] font-bold text-white/40 uppercase tracking-widest">Camera</span>
            </div>
            {videoDevices.length === 0 ? (
                <p className="text-xs text-white/20 px-1">No cameras found</p>
            ) : (
                <div className="grid grid-cols-2 gap-2">
                    {videoDevices.map((d, i) => {
                        const label = d.label || `Camera ${i + 1}`
                        const isSelected = selectedCameraId ? selectedCameraId === d.deviceId : i === 0
                        return (
                            <button key={d.deviceId} onClick={() => onSelectCamera(d.deviceId)}
                                className={`flex flex-col gap-2 p-2.5 rounded-xl border-2 text-left transition-all ${
                                    isSelected ? 'border-brand-500 bg-brand-500/10' : 'border-white/[0.07] bg-white/[0.03] hover:border-white/20'
                                }`}>
                                <div className="w-full aspect-video rounded-lg bg-black/50 flex items-center justify-center relative">
                                    <Camera size={18} className={isSelected ? 'text-brand-400' : 'text-white/20'} />
                                    {isSelected && (
                                        <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-brand-500 flex items-center justify-center">
                                            <Check size={9} className="text-white" />
                                        </div>
                                    )}
                                </div>
                                <span className={`text-[10px] font-medium truncate px-0.5 ${isSelected ? 'text-white' : 'text-white/50'}`}>
                                    {label}
                                </span>
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function DesktopHomePage() {
    const { status, duration, sources, loadSources, startRecording, pauseRecording, resumeRecording, stopRecording } = useDesktopRecorder()

    const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null)
    const [cropRect, setCropRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null)
    const [activePanel, setActivePanel] = useState<'picker' | 'projects' | 'mic' | 'camera' | null>(null)
    const [isLoadingSources, setIsLoadingSources] = useState(false)
    const [micEnabled, setMicEnabled] = useState(true)
    const [frameRate, setFrameRate] = useState<30 | 60>(60)

    // Device selection
    const [cameraEnabled, setCameraEnabled] = useState(false)
    const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null)
    const [selectedMicId, setSelectedMicId] = useState<string | null>(null)
    const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([])
    const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([])
    const [systemAudioEnabled, setSystemAudioEnabled] = useState(false)
    const [countdownDelay, setCountdownDelay] = useState(3)

    const [savePath, setSavePath] = useState('~/Videos')
    const [lastVideoPath, _setLastVideoPath] = useState<string | null>(null)
    const [showLocationSetup, setShowLocationSetup] = useState(false)
    const [pendingLocationPath, setPendingLocationPath] = useState<string | null>(null)

    const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)

    const isIdle = status === 'idle'
    const isRecording = status === 'recording'
    const isPaused = status === 'paused'
    const isProcessing = status === 'processing'
    const selectedSource = sources.find(s => s.id === selectedSourceId)
    const showPanel = activePanel !== null

    // Notify tray of recording state changes
    useEffect(() => {
        const state = isRecording ? 'recording' : isPaused ? 'paused' : 'idle'
        el()?.notifyRecordingState?.(state as any)
    }, [isRecording, isPaused])

    // Tray action refs — populated after handleStop is defined below
    const handleStopRef = useRef<() => void>(() => {})
    const pauseRef = useRef<() => void>(() => {})
    const resumeRef = useRef<() => void>(() => {})

    useEffect(() => {
        const cleanup = el()?.onTrayAction?.((action: string) => {
            if (action === 'stop') handleStopRef.current()
            else if (action === 'pause') pauseRef.current()
            else if (action === 'resume') resumeRef.current()
        })
        return () => cleanup?.()
    }, [])

    // Switch between full idle bar and mini recording pill
    useEffect(() => {
        if (isRecording || isPaused || isProcessing) {
            el()?.toRecordingBar?.(MINI_W, MINI_H)
        } else {
            el()?.toIdleBar?.(BAR_W, BAR_H)
        }
    }, [isRecording, isPaused, isProcessing])

    // Resize window when panel opens/closes (only relevant in idle mode)
    useEffect(() => {
        if (isRecording || isPaused || isProcessing) return
        const h = showLocationSetup ? BAR_H + 180
            : (activePanel === 'mic' || activePanel === 'camera') ? DEVICES_H
            : showPanel ? PANEL_H
            : BAR_H
        el()?.setWindowSize?.(BAR_W, h, false, true)
    }, [showPanel, activePanel, showLocationSetup, isRecording, isPaused, isProcessing])

    useEffect(() => {
        el()?.toIdleBar?.(BAR_W, BAR_H)
        refreshSources()
        loadDevices()
        // Re-enumerate after user grants mic/camera permission (labels become available)
        navigator.mediaDevices.addEventListener('devicechange', loadDevices)

        // Check if user has chosen a save location before
        el()?.getSetting?.('projectsPath').then((savedPath: string | null) => {
            if (savedPath) {
                setSavePath('~/' + savedPath.replace(/\\/g, '/').split('/').pop())
            } else {
                // First time — show setup dialog with default path pre-filled
                el()?.getVideosPath?.().then((defaultPath: string) => {
                    if (defaultPath) {
                        const defaultProjectsPath = defaultPath.replace(/\\/g, '/') + '/screenrecord.ing'
                        setPendingLocationPath(defaultProjectsPath)
                        setSavePath('~/' + defaultPath.replace(/\\/g, '/').split('/').pop() + '/screenrecord.ing')
                    }
                    setShowLocationSetup(true)
                })
            }
        })

        return () => { navigator.mediaDevices.removeEventListener('devicechange', loadDevices) }
    }, [])

    const loadDevices = useCallback(async () => {
        try {
            // Request mic + camera permission upfront so the OS dialog appears early
            // and enumerateDevices returns proper device labels.
            try {
                const s = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
                s.getTracks().forEach(t => t.stop())
            } catch {}
            const all = await navigator.mediaDevices.enumerateDevices()
            setAudioDevices(all.filter(d => d.kind === 'audioinput'))
            setVideoDevices(all.filter(d => d.kind === 'videoinput'))
        } catch (err) {
            console.warn('[DesktopHomePage] enumerateDevices failed:', err)
        }
    }, [])

    const refreshSources = useCallback(async () => {
        setIsLoadingSources(true)
        await loadSources()
        // Do NOT auto-select — user must explicitly choose a source
        setIsLoadingSources(false)
    }, [loadSources])

    const mm = String(Math.floor(duration / 60)).padStart(2, '0')
    const ss = String(duration % 60).padStart(2, '0')

    // Active device labels for bar display
    const activeMic = audioDevices.find(d => d.deviceId === selectedMicId) ?? audioDevices[0]
    const activeMicLabel = activeMic?.label ? truncate(activeMic.label, 18) : null
    const activeCamera = videoDevices.find(d => d.deviceId === selectedCameraId) ?? videoDevices[0]
    const activeCameraLabel = activeCamera?.label ? truncate(activeCamera.label, 18) : null

    function truncate(s: string, n: number) { return s.length > n ? s.slice(0, n) + '…' : s }

    const togglePanel = (panel: 'picker' | 'projects' | 'mic' | 'camera') => {
        setActivePanel(v => v === panel ? null : panel)
    }

    const handleRecord = () => {
        if (!selectedSourceId) return
        setActivePanel(null)
        if (countdownDelay === 0) { doStart(); return }

        // Hide HUD and show a centered overlay — the overlay's own JS counts down
        el()?.hideWindow?.()
        el()?.showCountdown?.(countdownDelay)

        let remaining = countdownDelay
        countdownRef.current = setInterval(() => {
            remaining -= 1
            if (remaining <= 0) {
                clearInterval(countdownRef.current!); countdownRef.current = null
                el()?.hideCountdown?.()
                doStart() // toRecordingBar will surface the window again
            }
        }, 1000)
    }

    const handleSelectArea = async () => {
        setActivePanel(null)
        el()?.hideWindow?.()
        try {
            const rect = await el()?.showAreaSelector?.()
            if (rect) {
                setCropRect(rect)
                // Auto-select the primary screen source so getDisplayMedia captures the full display
                const firstScreen = sources.find(s => s.id.startsWith('screen:'))
                if (firstScreen) setSelectedSourceId(firstScreen.id)
                else {
                    const loaded = await loadSources()
                    const scr = loaded.find((s: any) => s.id.startsWith('screen:'))
                    if (scr) setSelectedSourceId(scr.id)
                }
            }
        } finally {
            el()?.toIdleBar?.(BAR_W, BAR_H)
        }
    }

    const doStart = async () => {
        if (!selectedSourceId) return
        try {
            await startRecording({
                sourceId: selectedSourceId,
                audio: micEnabled,
                frameRate,
                micDeviceId: selectedMicId ?? undefined,
                cameraDeviceId: cameraEnabled ? (selectedCameraId ?? videoDevices[0]?.deviceId) : undefined,
                systemAudio: systemAudioEnabled,
                cropRect: cropRect ?? undefined,
            })
        } catch (err) {
            console.error(err)
            // Recover: show idle bar again if recording failed to start
            el()?.toIdleBar?.(BAR_W, BAR_H)
        }
    }

    const handleStop = () => { stopRecording() }
    handleStopRef.current = handleStop
    pauseRef.current = pauseRecording
    resumeRef.current = resumeRecording

    const handlePickFolder = async (fromSetup = false) => {
        const picked = await el()?.pickFolder?.()
        if (!picked) return
        const projectsPath = picked.replace(/\\/g, '/') + '/screenrecord.ing'
        await el()?.setSetting?.('projectsPath', projectsPath)
        setSavePath('~/' + picked.replace(/\\/g, '/').split('/').pop())
        setPendingLocationPath(projectsPath)
        if (fromSetup) setShowLocationSetup(false)
    }

    const confirmLocation = async () => {
        const pathToSave = pendingLocationPath
        if (pathToSave) await el()?.setSetting?.('projectsPath', pathToSave)
        setShowLocationSetup(false)
    }

    const srcLabel = cropRect
        ? `${cropRect.w}×${cropRect.h}`
        : selectedSource
            ? (selectedSource.name.length > 20 ? selectedSource.name.slice(0, 20) + '…' : selectedSource.name)
            : ''

    return (
        // Root: no background — transparent window shows only the pill bar
        <div className="w-full select-none flex flex-col">

            {/* ── Mini recording pill (shown when recording / paused / processing) ── */}
            <AnimatePresence mode="wait">
                {(isRecording || isPaused || isProcessing) && (
                    <motion.div
                        key="mini"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.15, ease: 'easeOut' }}
                        className="h-[48px] flex items-center px-2.5 bg-[#0E0E10] border border-white/[0.08] rounded-2xl"
                        style={{ WebkitAppRegion: 'drag' } as any}
                    >
                        <div className="flex items-center gap-1" style={{ WebkitAppRegion: 'no-drag' } as any}>
                            {isProcessing ? (
                                <Loader2 size={12} className="animate-spin text-white/30" />
                            ) : (
                                <button onClick={handleStop} title="Stop recording"
                                    className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:text-white hover:bg-red-500 transition-all active:scale-95">
                                    <Square size={11} fill="currentColor" />
                                </button>
                            )}

                            <span className={`text-xs font-bold tabular-nums px-1 ${isProcessing ? 'text-white/20' : isPaused ? 'text-white/30' : 'text-white/80'}`}>
                                {mm}:{ss}
                            </span>

                            {!isProcessing && (
                                <button onClick={() => isPaused ? resumeRecording() : pauseRecording()}
                                    title={isPaused ? 'Resume' : 'Pause'}
                                    className="w-7 h-7 flex items-center justify-center rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-all active:scale-95">
                                    {isPaused ? <Play size={10} fill="currentColor" /> : <Pause size={10} fill="currentColor" />}
                                </button>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Full idle bar (shown when idle) ── */}
            <AnimatePresence mode="wait">
                {!isRecording && !isPaused && !isProcessing && (
                    <motion.div
                        key="full"
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.97 }}
                        transition={{ duration: 0.15, ease: 'easeOut' }}
                        className="h-[56px] flex items-center px-3 gap-0.5 bg-[#0E0E10] border border-white/[0.08] rounded-2xl overflow-visible"
                        style={{ WebkitAppRegion: 'drag' } as any}
                    >
                {/* LEFT: source + mic + timer + fps */}
                <div className="flex items-center gap-0.5 shrink-0" style={{ WebkitAppRegion: 'no-drag' } as any}>
                    {(selectedSource || cropRect) ? (
                        /* ── Selected: active badge style ── */
                        <button
                            onClick={() => togglePanel('picker')}
                            className={`flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-xs font-semibold transition-all border ${
                                activePanel === 'picker'
                                    ? 'bg-brand-500/20 text-brand-300 border-brand-500/30'
                                    : 'bg-white/[0.07] text-white border-white/[0.10] hover:bg-white/[0.11]'
                            }`}>
                            {cropRect
                                ? <Crop size={12} className="shrink-0 text-brand-400" />
                                : selectedSource?.id.startsWith('screen:')
                                    ? <Monitor size={12} className="shrink-0 text-brand-400" />
                                    : <AppWindow size={12} className="shrink-0 text-brand-400" />}
                            <span className="truncate max-w-[120px]">{srcLabel}</span>
                            <ChevronDown size={10} className={`shrink-0 transition-transform text-white/40 ${activePanel === 'picker' ? 'rotate-180' : ''}`} />
                        </button>
                    ) : (
                        /* ── Nothing selected: call-to-action ── */
                        <button
                            onClick={() => togglePanel('picker')}
                            className={`flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-xs font-semibold transition-all border ${
                                activePanel === 'picker'
                                    ? 'bg-brand-500/20 text-brand-300 border-brand-500/30'
                                    : 'text-white/50 border-dashed border-white/20 hover:border-white/40 hover:text-white/80'
                            }`}>
                            <Monitor size={12} className="shrink-0 text-white/30" />
                            <span>Select Screen</span>
                            <ChevronDown size={10} className={`shrink-0 text-white/25 transition-transform ${activePanel === 'picker' ? 'rotate-180' : ''}`} />
                        </button>
                    )}

                    <Div />

                    {/* Mic — icon toggles on/off, chevron opens panel */}
                    <div className={`flex items-center h-8 rounded-lg border overflow-hidden transition-all ${
                        activePanel === 'mic'
                            ? 'bg-brand-500/20 text-brand-300 border-brand-500/30'
                            : micEnabled && activeMicLabel
                                ? 'bg-brand-500/10 border-brand-500/25 text-brand-300'
                                : micEnabled
                                    ? 'bg-white/[0.04] border-white/[0.06] text-white/70'
                                    : 'bg-white/[0.04] border-white/[0.06] text-white/25'
                    }`}>
                        <button onClick={() => setMicEnabled(v => !v)} title={activeMicLabel ? `${micEnabled ? 'Mute' : 'Unmute'}: ${activeMicLabel}` : micEnabled ? 'Mute mic' : 'Unmute mic'}
                            className="flex items-center justify-center w-8 h-full hover:bg-white/8 transition-colors">
                            {micEnabled ? <Mic size={12} className="shrink-0" /> : <MicOff size={12} className="shrink-0" />}
                        </button>
                        <button onClick={() => togglePanel('mic')} title="Mic settings"
                            className="flex items-center justify-center w-5 h-full hover:bg-white/8 transition-colors border-l border-white/[0.06]">
                            <ChevronDown size={9} className={`shrink-0 opacity-50 transition-transform ${activePanel === 'mic' ? 'rotate-180' : ''}`} />
                        </button>
                    </div>

                    {/* Camera — icon toggles on/off, chevron opens panel */}
                    <div className={`flex items-center h-8 rounded-lg border overflow-hidden transition-all ${
                        activePanel === 'camera'
                            ? 'bg-brand-500/20 text-brand-300 border-brand-500/30'
                            : cameraEnabled && activeCameraLabel
                                ? 'bg-brand-500/10 border-brand-500/25 text-brand-300'
                                : cameraEnabled
                                    ? 'bg-white/[0.04] border-white/[0.06] text-brand-400'
                                    : 'bg-white/[0.04] border-white/[0.06] text-white/25'
                    }`}>
                        <button onClick={() => setCameraEnabled(v => !v)} title={activeCameraLabel ? `${cameraEnabled ? 'Disable' : 'Enable'}: ${activeCameraLabel}` : cameraEnabled ? 'Disable camera' : 'Enable camera'}
                            className="flex items-center justify-center w-8 h-full hover:bg-white/8 transition-colors">
                            <Camera size={12} className="shrink-0" />
                        </button>
                        <button onClick={() => togglePanel('camera')} title="Camera settings"
                            className="flex items-center justify-center w-5 h-full hover:bg-white/8 transition-colors border-l border-white/[0.06]">
                            <ChevronDown size={9} className={`shrink-0 opacity-50 transition-transform ${activePanel === 'camera' ? 'rotate-180' : ''}`} />
                        </button>
                    </div>

                    {/* System audio toggle */}
                    <button
                        onClick={() => setSystemAudioEnabled(v => !v)}
                        title={systemAudioEnabled ? 'System audio on (captures desktop audio)' : 'System audio off'}
                        className={`flex items-center justify-center w-8 h-8 rounded-lg border transition-all ${
                            systemAudioEnabled
                                ? 'bg-brand-500/10 border-brand-500/25 text-brand-300'
                                : 'bg-white/[0.04] border-white/[0.06] text-white/25 hover:text-white/50'
                        }`}>
                        {systemAudioEnabled ? <Volume2 size={12} /> : <VolumeX size={12} />}
                    </button>

                    {/* Countdown — +/- stepper */}
                    <div className="flex items-center gap-0.5 h-8 px-1.5 rounded-lg bg-white/[0.04] border border-white/[0.06]" title="Countdown delay">
                        <Timer size={11} className="text-white/25 mr-0.5" />
                        <button onClick={() => setCountdownDelay(v => Math.max(0, v - 1))}
                            className="w-5 h-5 flex items-center justify-center rounded text-white/30 hover:text-white hover:bg-white/8 text-sm font-bold transition-all leading-none">
                            −
                        </button>
                        <span className="text-xs font-semibold text-white/40 w-5 text-center tabular-nums">{countdownDelay}s</span>
                        <button onClick={() => setCountdownDelay(v => Math.min(15, v + 1))}
                            className="w-5 h-5 flex items-center justify-center rounded text-white/30 hover:text-white hover:bg-white/8 text-sm font-bold transition-all leading-none">
                            +
                        </button>
                    </div>

                    {/* FPS — segmented toggle */}
                    <div className="flex items-center bg-white/[0.04] border border-white/[0.06] rounded-lg p-0.5" title="Frame rate">
                        {([30, 60] as const).map(fps => (
                            <button key={fps} onClick={() => setFrameRate(fps)}
                                className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                                    frameRate === fps ? 'bg-brand-500/25 text-brand-300' : 'text-white/30 hover:text-white/60'
                                }`}>
                                {fps}
                            </button>
                        ))}
                    </div>
                </div>

                {/* CENTER CTA */}
                <div className="flex-1 flex justify-center items-center" style={{ WebkitAppRegion: 'no-drag' } as any}>
                    {isIdle && (
                        <motion.button key="start"
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            onClick={handleRecord} disabled={!selectedSourceId}
                            whileTap={{ scale: selectedSourceId ? 0.97 : 1 }}
                            className={`flex items-center gap-2 h-9 px-5 rounded-full text-sm font-black tracking-tight transition-all ${
                                selectedSourceId
                                    ? 'bg-brand-500 hover:bg-brand-400 text-white shadow-lg shadow-brand-500/20 cursor-pointer'
                                    : 'bg-white/[0.06] text-white/25 cursor-not-allowed'
                            }`}>
                            {selectedSourceId && (
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                                </span>
                            )}
                            Start Recording
                        </motion.button>
                    )}
                </div>

                {/* RIGHT: save path + last recording + projects + close */}
                <div className="flex items-center gap-0.5 shrink-0" style={{ WebkitAppRegion: 'no-drag' } as any}>
                    <Div />

                    <button onClick={() => handlePickFolder(false)} title={`Save to: ${savePath}`}
                        className="flex items-center gap-1 h-8 px-2 rounded-lg text-[10px] font-mono text-white/30 hover:text-white hover:bg-white/8 transition-all max-w-[90px]">
                        <FolderOpen size={11} className="shrink-0" />
                        <span className="truncate">{savePath}</span>
                    </button>

                    <Div />

                    <BarBtn
                        onClick={() => lastVideoPath && el()?.revealInExplorer?.(lastVideoPath)}
                        active={!!lastVideoPath}
                        title="Open last recording in folder">
                        <Film size={13} />
                    </BarBtn>

                    <BarBtn
                        onClick={() => togglePanel('projects')}
                        active={activePanel === 'projects'}
                        title="Projects">
                        <LayoutGrid size={13} />
                    </BarBtn>

                    <Div />

                    <BarBtn onClick={() => el()?.closeWindow?.()} danger title="Close">
                        <X size={12} />
                    </BarBtn>
                </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── First-time save location setup ── */}
            <AnimatePresence>
                {showLocationSetup && (
                    <motion.div
                        initial={{ opacity: 0, y: -8, scaleY: 0.92 }}
                        animate={{ opacity: 1, y: 0, scaleY: 1 }}
                        exit={{ opacity: 0, y: -8, scaleY: 0.92 }}
                        transition={{ duration: 0.16, ease: 'easeOut' }}
                        style={{ transformOrigin: 'top' }}
                        className="mt-2 bg-[#0E0E10] border border-white/[0.08] rounded-2xl overflow-hidden p-5 space-y-4"
                    >
                        <div className="space-y-1">
                            <p className="text-sm font-semibold text-white">Where should recordings be saved?</p>
                            <p className="text-[11px] text-white/40 leading-relaxed">
                                Choose a folder on your computer. All recordings and project data will be stored here.
                            </p>
                        </div>

                        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06]">
                            <FolderOpen size={13} className="text-white/30 shrink-0" />
                            <span className="text-[11px] font-mono text-white/50 flex-1 truncate">{savePath}</span>
                            <button
                                onClick={() => handlePickFolder(true)}
                                className="text-[11px] text-brand-400 hover:text-brand-300 font-medium shrink-0 transition-colors"
                            >
                                Browse…
                            </button>
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={confirmLocation}
                                className="flex-1 h-8 rounded-xl bg-brand-500 hover:bg-brand-400 text-white text-xs font-semibold transition-colors"
                            >
                                Confirm
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Drop-down panel (source picker OR projects OR devices) ── */}
            <AnimatePresence>
                {activePanel && (
                    <motion.div
                        initial={{ opacity: 0, y: -8, scaleY: 0.92 }}
                        animate={{ opacity: 1, y: 0, scaleY: 1 }}
                        exit={{ opacity: 0, y: -8, scaleY: 0.92 }}
                        transition={{ duration: 0.14, ease: 'easeOut' }}
                        style={{
                            transformOrigin: 'top',
                            height: (activePanel === 'mic' || activePanel === 'camera') ? DEVICES_H - BAR_H - 8 : PANEL_H - BAR_H - 8,
                        }}
                        className="mt-2 bg-[#0E0E10] border border-white/[0.08] rounded-2xl overflow-hidden"
                    >
                        {activePanel === 'picker' && (
                            <SourcePickerPanel
                                sources={sources} selected={selectedSourceId} isLoading={isLoadingSources}
                                onSelect={id => { setSelectedSourceId(id); setCropRect(null); setActivePanel(null) }}
                                onRefresh={refreshSources}
                                cropRect={cropRect}
                                onSelectArea={handleSelectArea}
                            />
                        )}
                        {activePanel === 'projects' && (
                            <ProjectsPanel onClose={() => setActivePanel(null)} />
                        )}
                        {activePanel === 'mic' && (
                            <MicPanel
                                audioDevices={audioDevices}
                                selectedMicId={selectedMicId}
                                onSelectMic={setSelectedMicId}
                            />
                        )}
                        {activePanel === 'camera' && (
                            <CameraPanel
                                videoDevices={videoDevices}
                                selectedCameraId={selectedCameraId}
                                onSelectCamera={(id) => { setSelectedCameraId(id); setCameraEnabled(true) }}
                            />
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
